import { useEffect, useState } from 'react';
import { Alert, Input } from '@piggytechng/component-library';
import ModalBase from '../../../../../../../components/Modals';
import { OverideInputStyle } from '../../../../../../../helpers/utils';
import { SafelockSearchModalProps, SafelockSearchParams } from '../../../../../../../types';
import './SafelockSearchModal.scss';

export const EMPTY_SAFELOCK_SEARCH: SafelockSearchParams = {
  rand: '',
  amount: '',
  name: '',
  createdFrom: '',
  createdTo: '',
  paybackFrom: '',
  paybackTo: '',
};

const InputStyle = { ...OverideInputStyle, labelStyle: { color: '#666766' } };

function SafelockSearchModal({ modalIsOpen, setIsOpen, data, searchDataBy, onClear }: SafelockSearchModalProps) {
  const [filters, setFilters] = useState<SafelockSearchParams>({ ...EMPTY_SAFELOCK_SEARCH });

  useEffect(() => {
    if (modalIsOpen) setFilters({ ...EMPTY_SAFELOCK_SEARCH, ...data });
  }, [modalIsOpen, data]);

  const closeModal = () => setIsOpen(false);

  const setField = (key: keyof SafelockSearchParams) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setFilters((prev) => ({ ...prev, [key]: e.target.value }));

  const hasFilter = Object.values(filters).some((value) => value.trim() !== '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!hasFilter) {
      Alert.error('Enter at least one search filter');
      return;
    }

    if (filters.createdFrom && filters.createdTo && filters.createdFrom > filters.createdTo) {
      Alert.error('Created from date cannot be after the created to date');
      return;
    }

    if (filters.paybackFrom && filters.paybackTo && filters.paybackFrom > filters.paybackTo) {
      Alert.error('Payback from date cannot be after the payback to date');
      return;
    }

    // Empty values are dropped by queryParamBuilder, so only what was filled is sent.
    searchDataBy({
      ...filters,
      rand: filters.rand.trim(),
      name: filters.name.trim(),
      amount: filters.amount.trim(),
    });
    closeModal();
  };

  const handleClear = () => {
    setFilters({ ...EMPTY_SAFELOCK_SEARCH });
    onClear();
    closeModal();
  };

  return (
    <ModalBase modalIsOpen={modalIsOpen} setIsOpen={setIsOpen}>
      <div className="modal-search safelock-search">
        <div className="modal-search__header">
          <h2>Search Safelocks</h2>
          <button type="button" onClick={closeModal}>
            &times;
          </button>
        </div>

        <form className="modal-search__body" onSubmit={handleSubmit}>
          <Input
            {...InputStyle}
            type="text"
            label="Safelock ID"
            placeholder="e.g. SL9k2mXp4QwT7vNc"
            value={filters.rand}
            onChange={setField('rand')}
          />

          <Input
            {...InputStyle}
            type="text"
            label="Title"
            placeholder="e.g. House rent"
            value={filters.name}
            onChange={setField('name')}
          />

          <Input
            {...InputStyle}
            type="number"
            label="Amount"
            placeholder="e.g. 50000"
            value={filters.amount}
            onChange={setField('amount')}
          />

          <div className="safelock-search__range">
            <Input
              {...InputStyle}
              type="date"
              label="Created from"
              value={filters.createdFrom}
              onChange={setField('createdFrom')}
            />
            <Input
              {...InputStyle}
              type="date"
              label="Created to"
              value={filters.createdTo}
              onChange={setField('createdTo')}
            />
          </div>

          <div className="safelock-search__range">
            <Input
              {...InputStyle}
              type="date"
              label="Payback from"
              value={filters.paybackFrom}
              onChange={setField('paybackFrom')}
            />
            <Input
              {...InputStyle}
              type="date"
              label="Payback to"
              value={filters.paybackTo}
              onChange={setField('paybackTo')}
            />
          </div>

          <div className="modal-search__actions">
            <button
              className="modal-search__actions-reject"
              type="button"
              onClick={hasFilter ? handleClear : closeModal}
            >
              {hasFilter ? 'Clear' : 'Close'}
            </button>
            <button className="modal-search__actions-resolve" type="submit">
              Search
            </button>
          </div>
        </form>
      </div>
    </ModalBase>
  );
}

export default SafelockSearchModal;
